import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Loader2, MailCheck } from "lucide-react";

/** Forgot-password page. Always shows the same success message even
 * if the email isn't registered, so attackers can't enumerate
 * accounts. */
export default function ForgotPassword() {
  const { lang } = useI18n();
  const isAr = lang === "ar";
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const t = {
    title: isAr ? "نسيت كلمة المرور" : "Forgot your password",
    lead: isAr
      ? "أدخل بريدك الإلكتروني وسنرسل لك رابط إعادة التعيين."
      : "Enter your email and we'll send you a reset link.",
    email: isAr ? "البريد الإلكتروني" : "Email",
    submit: isAr ? "إرسال الرابط" : "Send reset link",
    sentTitle: isAr ? "تحقّق من بريدك" : "Check your inbox",
    sentBody: isAr
      ? "إذا كان البريد مسجّلًا لدينا، فستجد رابط إعادة التعيين خلال دقائق. صالح لـ 30 دقيقة."
      : "If that email is registered, a reset link is on its way. It's valid for 30 minutes.",
    backLogin: isAr ? "العودة لتسجيل الدخول" : "Back to sign-in",
    err: isAr ? "حدث خطأ، حاول مرة أخرى." : "Something went wrong.",
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch {
      toast.error(t.err);
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="forgot-password-sent" className="max-w-md mx-auto px-6 py-32 text-center">
        <MailCheck className="w-14 h-14 mx-auto text-emerald-600" />
        <h1 className="font-display text-3xl mt-6 tracking-tight">{t.sentTitle}</h1>
        <p className="mt-3 text-slate-600 leading-relaxed">{t.sentBody}</p>
        <Link
          to="/login"
          className="inline-block mt-8 text-sm text-slate-700 underline underline-offset-4"
        >
          {t.backLogin}
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="forgot-password-page" className="max-w-md mx-auto px-6 py-24">
      <p className="eyebrow">Aldakheel Legal Group</p>
      <h1 className="font-display text-4xl mt-3 tracking-tight">{t.title}</h1>
      <p className="mt-3 text-slate-600">{t.lead}</p>

      <form onSubmit={onSubmit} className="mt-12 grid gap-5" data-testid="forgot-password-form">
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.email}</Label>
          <Input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="forgot-password-email"
            className="mt-2 rounded-sm h-11"
            dir="auto"
          />
        </div>
        <Button
          type="submit"
          disabled={busy || !email}
          data-testid="forgot-password-submit"
          className="rounded-sm h-12 bg-slate-900 hover:bg-slate-800 text-white"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t.submit}
        </Button>
        <Link to="/login" className="text-sm text-slate-500 underline underline-offset-4 text-center">
          {t.backLogin}
        </Link>
      </form>
    </div>
  );
}
